import { getDeviceId } from './version'
import { smmFetch } from './http'

export const API = (import.meta.env.VITE_SMM_API as string | undefined) ?? 'https://smm-api.colectiva.casa'

export interface DraftSummary {
  id: string
  status: 'new' | 'editing' | 'approved'
  projectVersion: number
  warnings: string[]
  chosenClipIds: string[]
  createdAt: string
  updatedAt: string
}

export interface CatalogClip {
  id: string
  durationSec: number
  width: number
  height: number
  fps: number
  category: string | null
  description: string | null
  keywords: string[]
  hasThumbnail: boolean
}

export interface SaveResult {
  version: number
  conflict: boolean
}

export const clipFileUrl = (id: string): string => `${API}/clips/${id}/file`
export const clipThumbUrl = (id: string): string => `${API}/clips/${id}/thumb`

export async function listDrafts(): Promise<DraftSummary[]> {
  const r = await smmFetch(`/api/drafts`)
  if (!r.ok) throw new Error(`drafts: HTTP ${r.status}`)
  return (await r.json()).drafts as DraftSummary[]
}

export async function getDraftProject(id: string): Promise<Record<string, unknown>> {
  const r = await smmFetch(`/api/drafts/${id}/project`)
  if (!r.ok) throw new Error(`project ${id}: HTTP ${r.status}`)
  return r.json()
}

export async function putDraftProject(
  id: string,
  project: unknown,
  expectedVersion?: number,
): Promise<SaveResult> {
  const headers: Record<string, string> = { 'content-type': 'application/json', 'x-device-id': getDeviceId() }
  if (expectedVersion !== undefined) headers['if-match'] = String(expectedVersion)
  const r = await smmFetch(`/api/drafts/${id}/project`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(project),
  })
  if (!r.ok) throw new Error(`save ${id}: HTTP ${r.status}`)
  return r.json() as Promise<SaveResult>
}

export async function putDraftExport(id: string, mp4: Blob): Promise<{ ok: boolean; exportR2Key: string }> {
  const r = await smmFetch(`/api/drafts/${id}/export`, {
    method: 'PUT',
    headers: { 'content-type': mp4.type || 'video/mp4' },
    body: mp4,
  })
  if (!r.ok) throw new Error(`export ${id}: HTTP ${r.status}`)
  return r.json()
}

export async function listCatalog(params: { keyword?: string; q?: string } = {}): Promise<CatalogClip[]> {
  const qs = new URLSearchParams()
  if (params.keyword) qs.set('keyword', params.keyword)
  if (params.q) qs.set('q', params.q)
  const r = await smmFetch(`/api/catalog/clips?${qs.toString()}`)
  if (!r.ok) throw new Error(`catalog: HTTP ${r.status}`)
  return (await r.json()).clips as CatalogClip[]
}

/** Fetch one catalog clip by id. Used by hydrate to enrich freecut MediaMetadata
 *  (tags, description, filename) when loading a draft fresh from the server. */
export async function getCatalogClip(id: string): Promise<CatalogClip> {
  const r = await smmFetch(`/clips/${encodeURIComponent(id)}`, {
    headers: { accept: 'application/json' },
  })
  if (!r.ok) {
    throw new Error(`getCatalogClip(${id}) failed with ${r.status}`)
  }
  return (await r.json()) as CatalogClip
}

/** Fetch one draft's summary (status, projectVersion, warnings). Used by the
 *  editor route to recover the version for autosave when deep-linking to a
 *  draft that hasn't been hydrated into OPFS yet. */
export async function getDraftSummary(id: string): Promise<DraftSummary> {
  const r = await smmFetch(`/api/drafts/${encodeURIComponent(id)}`)
  if (!r.ok) throw new Error(`getDraftSummary(${id}) failed with ${r.status}`)
  return (await r.json()).draft as DraftSummary
}
